import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { auth } from './firebase';
import configData from './configure';
import useUserStore from './state/userStore';
import { safeSetItem, safeRemoveItem } from './utils/storageUtils';
import { startPresenceHeartbeat, recordUserSession } from './utils/presenceService';

const AuthContext = createContext();

export const AuthProvider = ({ children, presentUser }) => {
  const storedToken = localStorage.getItem('token');
  const [token, setToken] = useState(storedToken || null);
  const [user, setUser] = useState(null);
  const [studentInfo, setStudentInfo] = useState(null);
  const [facultyInfo, setFacultyInfo] = useState(null);
  const [parentInfo, setParentInfo] = useState(null);
  const [staffInfo, setStaffInfo] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isValidated, setIsValidated] = useState(false);
  const [orgId, setOrgId] = useState('');
  const [parentOrgId, setParentOrgId] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);

  const axiosInstance = axios.create({
    headers: { 'Content-Type': 'application/json' },
  });

  axiosInstance.interceptors.request.use(
    (config) => {
      if (token) config.headers.Authorization = token;
      return config;
    },
    (error) => Promise.reject(error)
  );

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn('[AuthContext] 401/403 Unauthorized detected on API call. Logging out immediately.');
        performImmediateLogout();
      }
      return Promise.reject(error);
    }
  );

  // Cleanup function
  const cleanupAuth = () => {
    setUser(null);
    setStudentInfo(null);
    setFacultyInfo(null);
    setParentInfo(null);
    setStaffInfo(null);
    setUserRole(null);
    setPermissions([]);
    setIsValidated(false);
    setOrgId('');
    setParentOrgId('');
    setUnreadCount(0);
  };

  // Immediate logout
  const performImmediateLogout = () => {
    try {
      auth.signOut().catch(() => {});
    } catch (_) {}
    safeRemoveItem('token');
    safeRemoveItem('tokenExpiry');

    // Clear all global states
    cleanupAuth();

    // Use dynamic/lazy clearing to avoid circular dependencies
    try {
      const qClient = require('./queryClient').default;
      qClient.clear();

      const reduxStore = require('./state/store').default;
      reduxStore.dispatch({ type: 'RESET_STATE' });
    } catch (e) {
      console.warn('Delayed cleanup failed:', e);
    }

    useUserStore.getState().reset();

    setToken(null);
  };

  // Validate token + fetch user
  const validateTokenAndFetchUser = async (currentToken) => {
    setIsLoading(true);
    setIsValidated(false);

    // Proactively clear state before fetching new user data
    // to ensure no leakage from previous organization/session
    try {
      const qClient = require('./queryClient').default;
      qClient.clear();

      const reduxStore = require('./state/store').default;
      reduxStore.dispatch({ type: 'RESET_STATE' });
    } catch (e) {
      console.warn('Proactive cleanup failed:', e);
    }
    useUserStore.getState().reset();

    try {
      let email = '';
      let userId = '';
      try {
        const decoded = jwtDecode(currentToken);
        if (decoded.email && decoded.email.includes('@')) {
          email = decoded.email;
        } else if (decoded.sub && decoded.sub.includes('@')) {
          email = decoded.sub;
        } else {
          userId = decoded.sub;
        }
      } catch (e) {
        throw new Error('Invalid token format');
      }

      if (!email && !userId && presentUser?.email) {
        email = presentUser.email;
      }

      let response;
      try {
        if (email) {
          const queryEmail = encodeURIComponent(email.trim());
          response = await axiosInstance.get(
            `${configData.GET_USER_BY_EMAIL_WITH_PARAM}/${queryEmail}`,
            {
              headers: {
                accept: 'application/hal+json',
                Authorization: currentToken,
              },
            }
          );
        } else if (userId) {
          response = await axiosInstance.get(
            `${configData.GET_USER_DETAILS}/${userId}`,
            {
              headers: {
                accept: 'application/hal+json',
                Authorization: currentToken,
              },
            }
          );
        }
      } catch (apiErr) {
        if (apiErr.response?.status === 401 || apiErr.response?.status === 403) {
          console.warn('[AuthContext] User validation returned 401/403 Unauthorized. Logging out immediately.');
          performImmediateLogout();
          setIsLoading(false);
          return;
        }

        console.warn('Primary user validation request failed, attempting fallback:', apiErr.message);
        if (userId) {
          try {
            response = await axiosInstance.get(
              `${configData.GET_USER_DETAILS}/${userId}`,
              {
                headers: {
                  accept: 'application/hal+json',
                  Authorization: currentToken,
                },
              }
            );
          } catch (e2) {
            if (e2.response?.status === 401 || e2.response?.status === 403) {
              console.warn('[AuthContext] Fallback validation returned 401/403 Unauthorized. Logging out immediately.');
              performImmediateLogout();
              setIsLoading(false);
              return;
            }
            console.warn('Fallback user details fetch failed:', e2.message);
          }
        }
      }

      const rawData = response?.data || {};
      const userData = rawData.user?.userId
        ? rawData.user
        : (rawData.userId
          ? rawData
          : (Array.isArray(rawData)
            ? (rawData[0]?.user || rawData[0])
            : null));

      if (!userData || (!userData.userId && !userData.email) || (!userData.userType && !userData.role)) {
        throw new Error('User not registered or lacks valid role in system');
      }

      if (userData.userStatus === 'INACTIVE' || userData.userStatus === 'inactive') {
        throw new Error('This account has been deactivated.');
      }

      setUser(userData);
      setStudentInfo(rawData.studentInfo || response?.data?.studentInfo);
      setFacultyInfo(rawData.facultyInfo || response?.data?.facultyInfo);
      setParentInfo(rawData.parentInfo || response?.data?.parentInfo);
      setStaffInfo(rawData.staffInfo || response?.data?.staffInfo);
      setUserRole((userData.userType || userData.role || '').toUpperCase());

      // Initialize orgId and parentOrgId
      if (userData.orgId && !orgId) {
        setOrgId(userData.orgId);
      }
      const initialParentOrgId = userData.parentOrgId || userData.orgDetails?.parentOrgId || '';
      if (initialParentOrgId) {
        setParentOrgId(initialParentOrgId);
      } else if (userData.orgId) {
        axiosInstance.get(`${configData.GET_ORG_BY_ID}/${userData.orgId}`, {
          headers: { accept: 'application/hal+json', Authorization: currentToken }
        }).then(res => {
          if (res.data?.parentOrgId) {
            setParentOrgId(res.data.parentOrgId);
          }
        }).catch(err => console.warn('Failed to load parentOrgId:', err));
      }

      // Fetch permissions synchronously before stopping loading
      if (userData.userType) {
        await fetchRolePermissions(userData.userType, currentToken);
      }

      // Set validated and stop loading
      setIsValidated(true);
      setIsLoading(false);
    } catch (error) {
      console.error('Validation failed, clearing session and redirecting to login:', error.message);
      performImmediateLogout();
      setIsLoading(false);
    }
  };

  const fetchRolePermissions = async (roleId, currentToken) => {
    try {
      const activeToken = currentToken || token;
      const response = await axiosInstance.get(
        `${configData.GET_ROLE_PERMISSIONS}/${roleId}`,
        { headers: { accept: 'application/hal+json', Authorization: activeToken } }
      );
      const perms = response.data.permissionsList || [];
      setPermissions(perms);
      return perms;
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setPermissions([]);
      return [];
    }
  };

  const refreshUnreadCount = async () => {
    if (user?.userId && token) {
      try {
        const response = await axiosInstance.get(
          configData.INBOX_UNREAD_COUNT,
          { params: { userId: user.userId, orgId: user.orgId } }
        );
        setUnreadCount(response.data || 0);
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    }
  };

  useEffect(() => {
    if (isValidated && user?.userId) {
      refreshUnreadCount();
    }
  }, [isValidated, user?.userId, orgId]);

  // Main effect: handle token changes
  useEffect(() => {
    if (token) {
      safeSetItem('token', token);
      validateTokenAndFetchUser(token);
    } else {
      safeRemoveItem('token');
      // Already handled by performImmediateLogout if triggered via logout()
      // but safe to call again for redundancy if token is cleared elsewhere
      cleanupAuth();
      setIsLoading(false);
    }
  }, [token]);

  // Live presence heartbeat across tabs and windows
  useEffect(() => {
    if (user) {
      recordUserSession(user, window.location.pathname);
      const cleanup = startPresenceHeartbeat(user);
      return cleanup;
    }
  }, [user]);

  // Sync with Firebase auth state
  useEffect(() => {
    if (!presentUser && token) {
      // Firebase signed out but we still have a token/state
      // This happens if auth.signOut() is called directly elsewhere
      performImmediateLogout();
    }
  }, [presentUser, token]);

  // Global listener for 401 unauthorized events broadcast by apiClient or other services
  useEffect(() => {
    const handleUnauthorized = () => {
      console.warn('[AuthContext] auth:unauthorized event received. Performing immediate logout.');
      performImmediateLogout();
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  const logout = () => {
    performImmediateLogout();
  };

  const hasPermission = (module, action) => {
    if (userRole === 'ADMIN' || userRole === 'FACULTY') return true;

    // Explicitly allow students to VIEW classes and subjects for our restricted views
    if (userRole === 'STUDENT' && action === 'VIEW' && (module === 'CLASSES' || module === 'SUBJECTS')) {
      return true;
    }

    // Explicitly allow faculty to CREATE other faculty
    if (userRole === 'FACULTY' && action === 'CREATE' && module === 'FACULTY') {
      return true;
    }

    // Explicitly allow faculty to VIEW faculty module
    if (userRole === 'FACULTY' && action === 'VIEW' && module === 'FACULTY') {
      return true;
    }

    if (!module) return true; // If no module specified, just check if logged in (which we are if we reach here)
    const perm = permissions.find(p => p.module === module);
    return perm?.permissionActionsList.includes(action) || false;
  };

  const value = {
    token,
    setToken,
    user,
    studentInfo,
    facultyInfo,
    parentInfo,
    staffInfo,
    userRole,
    permissions,
    logout,
    axios: axiosInstance,
    isLoading,
    isValidated,
    orgId,
    setOrgId,
    parentOrgId,
    setParentOrgId,
    hasPermission,
    unreadCount,
    setUnreadCount,
    refreshUnreadCount,
    fetchUserInfo: () => token && validateTokenAndFetchUser(token),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};